import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, LinkedinLogo, DownloadSimple, BookmarkSimple, CaretUp, CaretDown,
  Trophy, Sparkle, ArrowClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { scoreColor, statusColor } from "@/lib/constants";
import ContactPanel from "@/components/ContactPanel";

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3">
    <p className="font-head text-2xl font-bold tracking-tight" style={accent ? { color: accent } : {}}>{value}</p>
    <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
  </div>
);

const Pill = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-2.5 h-7 rounded-md text-xs font-medium border transition-colors ${
      active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
    }`}>{children}</button>
);

export default function Results() {
  const { searchId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [active, setActive] = useState(null);
  const [sortKey, setSortKey] = useState("hiring_score");
  const [sortDir, setSortDir] = useState("desc");

  const [fSeniority, setFSeniority] = useState(null);
  const [fDept, setFDept] = useState(null);
  const [fScore, setFScore] = useState(0);
  const [fEmail, setFEmail] = useState(null);
  const [fLinkedin, setFLinkedin] = useState(null);

  useEffect(() => {
    const cached = sessionStorage.getItem(`search_${searchId}`);
    if (cached) {
      setData(JSON.parse(cached));
      setLoading(false);
    } else {
      api.get(`/search/${searchId}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load results")).finally(() => setLoading(false));
    }
  }, [searchId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = [...data.results];
    if (fSeniority) rows = rows.filter((r) => r.seniority === fSeniority);
    if (fDept) rows = rows.filter((r) => r.department === fDept || (fDept === "Performance" && r.department === "Performance Marketing"));
    if (fScore) rows = rows.filter((r) => r.hiring_score >= fScore);
    if (fEmail === "Verified") rows = rows.filter((r) => r.email_status === "Verified");
    if (fEmail === "Available") rows = rows.filter((r) => r.email);
    if (fEmail === "None") rows = rows.filter((r) => !r.email);
    if (fLinkedin === "Available") rows = rows.filter((r) => r.linkedin);
    if (fLinkedin === "None") rows = rows.filter((r) => !r.linkedin);
    rows.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") { av = (av || "").toLowerCase(); bv = (bv || "").toLowerCase(); }
      if (sortKey === "email_confidence") { av = av || 0; bv = bv || 0; }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [data, fSeniority, fDept, fScore, fEmail, fLinkedin, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "name" || k === "title" ? "asc" : "desc"); }
  };

  const toggleSel = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };

  const exportCsv = async () => {
    const chosen = data.results.filter((r) => selected.has(r.id));
    const contacts = chosen.length ? chosen : filtered;
    try {
      const res = await api.post("/export", { contacts }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "hiring_contacts.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${contacts.length} contacts`);
    } catch (e) { toast.error("Export failed"); }
  };

  const saveCompany = async () => {
    try {
      await api.post("/companies/save", { company: data.company, target_role: data.target_role });
      toast.success("Company saved");
    } catch (e) { toast.error("Could not save"); }
  };

  if (loading) return <div className="p-20 text-center text-zinc-400">Loading results…</div>;
  if (!data) return null;

  const s = data.summary;

  if (s.total === 0) {
    return (
      <div className="max-w-2xl mx-auto px-8 py-24 text-center">
        <h2 className="font-head text-2xl font-bold mb-3">No high-confidence hiring contacts found.</h2>
        <p className="text-zinc-500 mb-6">Try broadening your filters or a different role.</p>
        <button onClick={() => navigate("/app")} className="h-11 px-6 rounded-lg bg-zinc-900 text-white text-sm">Broaden Filters</button>
      </div>
    );
  }

  const SortHead = ({ k, children, align = "left" }) => (
    <th className={`px-3 py-2.5 text-${align} cursor-pointer select-none`} onClick={() => setSort(k)} data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        {children}
        {sortKey === k && (sortDir === "asc" ? <CaretUp size={12} /> : <CaretDown size={12} />)}
      </span>
    </th>
  );

  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button data-testid="back-button" onClick={() => navigate("/app")} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors">
          <ArrowLeft size={16} /> New Search
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="save-company-button" onClick={saveCompany} className="flex items-center gap-2 h-10 px-4 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50 transition-colors">
            <BookmarkSimple size={16} /> Save Company
          </button>
          <button data-testid="export-button" onClick={exportCsv} className="flex items-center gap-2 h-10 px-4 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-800 transition-colors">
            <DownloadSimple size={16} /> Export{selected.size ? ` (${selected.size})` : " Contacts"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-2">
        {data.company?.logo && <img src={data.company.logo} alt="" className="w-9 h-9 rounded-md object-contain" onError={(e) => (e.target.style.display = "none")} />}
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{data.company?.name}</h1>
          <p className="text-sm text-zinc-500">{data.target_role} · {data.company?.domain}{data.company?.size && data.company.size !== "Unknown" ? ` · ${data.company.size}` : ""}</p>
        </div>
      </div>

      {data.warning && (
        <div className="mb-3 text-xs px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 inline-block" data-testid="provider-warning">
          {data.warning}
        </div>
      )}
      {data.is_demo && (
        <div className="mb-5 text-xs px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 inline-block" data-testid="demo-banner">
          Showing <strong>demo data</strong>. Emails are illustrative and not verified. Connect Hunter or Apollo in Settings for live results.
        </div>
      )}

      <p className="font-head text-lg font-semibold mb-3">{s.total} relevant contacts found</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <StatCard label="High Priority" value={s.high_priority} accent="#10B981" />
        <StatCard label="Medium Priority" value={s.medium_priority} accent="#F59E0B" />
        <StatCard label="Recruiters" value={s.recruiters} />
        <StatCard label="Emails Available" value={s.emails_available} />
        <StatCard label="Verified Emails" value={s.verified_emails} />
        <StatCard label="LinkedIn Profiles" value={s.linkedin_profiles} />
      </div>

      {/* Recommended */}
      {data.recommended?.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={18} className="text-zinc-900" weight="fill" />
            <h2 className="font-head text-lg font-semibold">Recommended Contacts</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.recommended.map((p, i) => (
              <button key={p.id} data-testid={`recommended-${i}`} onClick={() => setActive(p)}
                className="text-left bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-zinc-400">#{i + 1}</span>
                  <span className="font-head font-bold text-sm" style={{ color: scoreColor(p.hiring_score) }}>{p.hiring_score}</span>
                </div>
                <p className="font-semibold text-sm truncate">{p.name}</p>
                <p className="text-xs text-zinc-500 truncate mb-2">{p.title}</p>
                <p className="text-xs text-zinc-400 line-clamp-2">{p.reason}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mr-1">Seniority</span>
        {["C-Level", "VP", "Head", "Director", "Manager", "Recruiter"].map((x) => (
          <Pill key={x} testid={`filter-sen-${x}`} active={fSeniority === x} onClick={() => setFSeniority(fSeniority === x ? null : x)}>{x}</Pill>
        ))}
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mx-1">Score</span>
        {[90, 80, 70].map((x) => (
          <Pill key={x} testid={`filter-score-${x}`} active={fScore === x} onClick={() => setFScore(fScore === x ? 0 : x)}>{x}+</Pill>
        ))}
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mx-1">Email</span>
        {["Verified", "Available", "None"].map((x) => (
          <Pill key={x} testid={`filter-email-${x}`} active={fEmail === x} onClick={() => setFEmail(fEmail === x ? null : x)}>{x}</Pill>
        ))}
        <Pill testid="filter-linkedin" active={fLinkedin === "Available"} onClick={() => setFLinkedin(fLinkedin === "Available" ? null : "Available")}>Has LinkedIn</Pill>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50/50">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  <input data-testid="select-all" type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} className="rounded border-zinc-300" />
                </th>
                <SortHead k="name">Name</SortHead>
                <SortHead k="title">Job Title</SortHead>
                <SortHead k="department">Department</SortHead>
                <SortHead k="seniority">Seniority</SortHead>
                <SortHead k="location">Location</SortHead>
                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Email</th>
                <SortHead k="email_status">Status</SortHead>
                <th className="px-3 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-zinc-500">LinkedIn</th>
                <SortHead k="hiring_score" align="right">Score</SortHead>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`row-${p.id}`} onClick={() => setActive(p)}
                  className="border-b border-zinc-100 hover:bg-zinc-50/70 cursor-pointer transition-colors">
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input data-testid={`select-${p.id}`} type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} className="rounded border-zinc-300" />
                  </td>
                  <td className="px-3 py-3 font-medium whitespace-nowrap">{p.name}</td>
                  <td className="px-3 py-3 text-zinc-600 whitespace-nowrap">{p.title}</td>
                  <td className="px-3 py-3"><span className="px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-700 text-xs font-medium whitespace-nowrap">{p.department}</span></td>
                  <td className="px-3 py-3 text-zinc-600">{p.seniority}</td>
                  <td className="px-3 py-3 text-zinc-600 whitespace-nowrap">{p.location}</td>
                  <td className="px-3 py-3 text-zinc-600 max-w-[180px] truncate">{p.email || "—"}</td>
                  <td className="px-3 py-3">
                    {p.email_status ? (
                      <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${statusColor(p.email_status)} whitespace-nowrap`}>
                        {p.email_status}{p.email_confidence ? ` ${p.email_confidence}%` : ""}
                      </span>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {p.linkedin ? (
                      <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex text-blue-600 hover:text-blue-800"><LinkedinLogo size={18} weight="fill" /></a>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-head font-bold" style={{ color: scoreColor(p.hiring_score) }}>{p.hiring_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="p-10 text-center text-sm text-zinc-400">No contacts match the current filters.</div>}
      </div>

      <ContactPanel person={active} onClose={() => setActive(null)} />
    </div>
  );
}
